import api from './api';
import { HomepageBanner, CreateBannerRequest, UpdateBannerRequest, FeaturedDesignerEntry, AddFeaturedDesignerRequest, UpdateFeaturedDesignerRequest, HomepageDressEntry, AddHomepageDressRequest } from '../types/homepage';
import { Designer } from './designer';

export const homepageService = {
  // Banner Management
  async getBanners(): Promise<HomepageBanner[]> {
    const response = await api.get('/homepage-banners/all');
    return response.data;
  },

  async createBanner(bannerData: CreateBannerRequest, imageFile: File): Promise<HomepageBanner> {
    const formData = new FormData();
    formData.append('media', imageFile);
    
    if (bannerData.title) formData.append('title', bannerData.title);
    if (bannerData.subtitle) formData.append('subtitle', bannerData.subtitle);
    if (bannerData.linkUrl) formData.append('linkUrl', bannerData.linkUrl);
    if (bannerData.linkText) formData.append('linkText', bannerData.linkText);
    if (bannerData.displayOrder !== undefined) formData.append('displayOrder', bannerData.displayOrder.toString());
    if (bannerData.isActive !== undefined) formData.append('isActive', bannerData.isActive.toString());

    const response = await api.post('/homepage-banners', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  async updateBanner(id: string, bannerData: UpdateBannerRequest, imageFile?: File): Promise<HomepageBanner> {
    const formData = new FormData();

    if (imageFile) formData.append('media', imageFile);
    if (bannerData.title !== undefined) formData.append('title', bannerData.title);
    if (bannerData.subtitle !== undefined) formData.append('subtitle', bannerData.subtitle);
    if (bannerData.linkUrl !== undefined) formData.append('linkUrl', bannerData.linkUrl);
    if (bannerData.linkText !== undefined) formData.append('linkText', bannerData.linkText);
    if (bannerData.displayOrder !== undefined) formData.append('displayOrder', bannerData.displayOrder.toString());
    if (bannerData.isActive !== undefined) formData.append('isActive', bannerData.isActive.toString());

    const response = await api.put(`/homepage-banners/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  async deleteBanner(id: string): Promise<void> {
    await api.delete(`/homepage-banners/${id}`);
  },

  async updateBannerOrder(banners: Array<{ id: string; displayOrder: number }>): Promise<void> {
    await api.put('/homepage-banners/order/update', { banners });
  },

  // Featured Designers Management
  async getFeaturedDesigners(): Promise<FeaturedDesignerEntry[]> {
    const response = await api.get('/featured-designers/all');
    return response.data;
  },

  async addFeaturedDesigner(designerData: AddFeaturedDesignerRequest): Promise<FeaturedDesignerEntry> {
    const response = await api.post('/featured-designers', designerData);
    return response.data;
  },

  async updateFeaturedDesigner(id: string, updates: UpdateFeaturedDesignerRequest): Promise<FeaturedDesignerEntry> {
    const response = await api.put(`/featured-designers/${id}`, updates);
    return response.data;
  },

  async removeFeaturedDesigner(id: string): Promise<void> {
    await api.delete(`/featured-designers/${id}`);
  },

  async removeFeaturedDesignerByDesignerId(designerId: string): Promise<void> {
    await api.delete(`/featured-designers/designer/${designerId}`);
  },

  async updateFeaturedDesignersOrder(designers: Array<{ id: string; displayOrder: number }>): Promise<void> {
    await api.put('/featured-designers/order/update', { designers });
  },

  // Homepage Dresses Management
  async getHomepageDresses(section: 'style_matcher' | 'collection'): Promise<HomepageDressEntry[]> {
    const response = await api.get(`/homepage-dresses/${section}/all`);
    return response.data;
  },

  async addHomepageDress(data: AddHomepageDressRequest): Promise<HomepageDressEntry> {
    const response = await api.post('/homepage-dresses', data);
    return response.data;
  },

  async removeHomepageDress(id: string): Promise<void> {
    await api.delete(`/homepage-dresses/${id}`);
  },

  async updateHomepageDressesOrder(section: 'style_matcher' | 'collection', dresses: Array<{ id: string; displayOrder: number }>): Promise<void> {
    await api.put(`/homepage-dresses/${section}/order`, { dresses });
  },

  // Get image URL - handles both S3 URLs and legacy local paths
  getImageUrl(path: string | undefined, s3Url?: string | undefined): string | undefined {
    // Prefer S3 URL if available
    if (s3Url) return s3Url;

    // Fallback to legacy local path
    if (!path) return undefined;
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';
    return `${baseUrl}/uploads/${path}`;
  },
};